<template>
    <div class="m-4 md:mx-8 md:mt-16">
        <section>
            <h1 class="px-4 md:px-8 py-2 text-2xl font-bold bg-primario text-gray-200 tracking-wide rounded-t">
                {{ title }} ({{ totalRows }})
            </h1>
            <div class="bg-white p-4 md:p-8 rounded-b-sm">
                <form class="flex flex-wrap md:flex-no-wrap justify-between md:space-x-3 space-y-3 md:space-y-0">
                    <div class="flex w-full md:max-w-xs flex-col space-y-3">
                        <div class="relative inline-block text-gray-500 focus-within:text-gray-700">
              <span class="absolute inset-y-0 right-0 outline-none">
                <svg
                    class="w-5 h-5 mr-2 mt-1.5 fill-current outline-none"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                      d="M16.32 14.9l5.39 5.4a1 1 0 0 1-1.42 1.4l-5.38-5.38a8 8 0 1 1 1.41-1.41zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"
                  />
                </svg>
              </span>
                            <input
                                v-model="filters.searchReference"
                                type="text"
                                :placeholder="$t('lang.msg.searchByReference')"
                                class="form-input w-full h-8 pr-8 appearance-none border border-gray-500 rounded py-2 pl-4 text-gray-900 placeholder-gray-500 leading-5 focus:outline-none focus:shadow-outline"
                            >
                        </div>
                        <Select
                            v-if="!hasRoles(['CLIENTE'])"
                            id="idClient"
                            :data="clients"
                            track-by="clientName"
                            :placeholder="$t('lang.msg.searchByClient')"
                            v-model.number="filters.idClient"
                            @input="changeFilter"
                        />
                        <Select
                            id="idCollection"
                            :data="collections"
                            :track-by="getLangLabel('collection')"
                            :placeholder="$t('lang.msg.searchByCollection')"
                            v-model.number="filters.idCollection"
                            @input="changeFilter"
                        />
                    </div>
                    <div class="flex flex-col justify-end w-full md:max-w-72 space-y-3">
                        <ButtonRedirect
                            route="newComercialRequest"
                            v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                        >
                            {{ $t("lang.msg.addRequest") }}
                        </ButtonRedirect>
                        <!-- === FILTROS === -->
                        <div class="flex space-x-2">
                            <ButtonAction large color="verde" @action="filterAction">
                                {{ $t("lang.msg.filter") }}
                            </ButtonAction>
                            <ButtonAction
                                large
                                color="red"
                                @action="removeFilters(`api/ComercialRequests/PaginationNoDesign`, `api/ComercialRequests/TotalRegistrosNoDesign`)"
                            >
                                {{ $t("lang.msg.removeFilters") }}
                            </ButtonAction>
                        </div>
                        <!-- ================================ -->
                        <!-- === ACTIVOS / DESCATALOGADOS === -->
                        <!-- ================================ -->
                        <ButtonAction
                            v-if="showingActivePlannings"
                            v-show="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                            large
                            color="lightgray"
                            @action="getDiscontinuedPlannings(`api/ComercialRequests/PaginationDescatalogada`, `api/ComercialRequests/TotalRegistrosGetDescatalogada`)"
                        >
                            {{ $t("lang.msg.viewDiscontinued") }}
                        </ButtonAction>
                        <ButtonAction
                            v-else
                            large
                            color="lightgray"
                            @action="removeFilters(`api/ComercialRequests/Pagination`, `api/ComercialRequests/TotalRegistros`)"
                        >
                            {{ $t("lang.msg.viewActivePlannings") }}
                        </ButtonAction>
                    </div>
                </form>


                <PlanningPagination
                    :current-page="page"
                    :total-rows="totalRows"
                    :visible-rows="visibleRows"
                    @next-page="fetchPaginationData('next');"
                    @previous-page="fetchPaginationData('previous');"
                    @load-selected-page="fetchPaginationData"
                    @take-rows="takeRows"
                />
                <!-- === PLANNING TABLE === -->
                <ul id="PlanningTable">
                    <draggable v-model="planning" :disabled="isSmallScreen">
                        <li
                            v-for="(item, index) in planning"
                            :key="`item_${index}`"
                            class="mt-6 pr-2 grid items-center grid-cols-1 sm:grid-cols-2 xl:grid-cols-comercial gap-0 sm:gap-4"
                            :class="{'bg-gray-200':index % 2 == 0,'border-red':item.descatalogada}"
                        >
                            <PlanningImages
                                :id-comercial-request="item.idComercialRequest"
                                :join-life="item.joinLife"
                                :reoperate="item.productionReoperation"
                                :official-delivery-date1="item.officialDeliveryDate1"
                                :reference-number="item.comercialRequestReferenceNumber"
                                :discontinued="item.descatalogada"
                                :cloned="item.cloned"
                            />
                            <div class="xl:space-y-2 cursor-pointer" @click="viewPlanning(index)">
                                <PlanningTableItem
                                    :index="index"
                                    :data="item.clientName"
                                    :label="$t('lang.msg.client')"
                                />
                                <PlanningTableItem
                                    :index="index"
                                    :data="
                  concatTwoValues(
                    item.comercialRequestReference,
                    item.comercialRequestReferenceNumber
                  )
                "
                                    :label="$t('lang.msg.reference')"
                                />
                                <PlanningTableItem
                                    :index="index"
                                    :data=" concatTwoValues(item.employeeName, item.employeeLastName) "
                                    :label="$t('lang.msg.internalSalesman')"
                                />
                            </div>
                            <div class="xl:space-y-2 cursor-pointer" @click="viewPlanning(index)">
                                <PlanningTableItem
                                    :index="index"
                                    :data="getLangValue(item, 'collection')"
                                    :label="$t('lang.msg.collection')"
                                />
                                <PlanningTableItem
                                    :index="index"
                                    :data="concatTwoValues(item.buyerName, item.buyerLastName)"
                                    :label="$t('lang.msg.buyer')"
                                />
                                <PlanningTableItem
                                    :index="index"
                                    :data=" concatTwoValues(item.designerName, item.designerLastName) "
                                    :label="$t('lang.msg.clientDesigner')"
                                />
                            </div>
                            <PlanningDays
                                :index="index"
                                class="cursor-pointer"
                                :delivery-date="item.comercialRequestNeededDate"
                                :activation-date="item.comercialRequestActivationDate"
                                :end-date="item.comercialToDesignDate"
                                :is-finished="item.comercialToDesign"
                                @click="viewPlanning(index)"
                            />
                            <div class="my-2">
                                <div
                                    class="flex justify-end items-center xl:flex-col space-x-4 xl:space-x-0 xl:space-y-3"
                                >
                                    <ButtonRounded
                                        title="Mover"
                                        color=""
                                        large
                                        v-if="!isSmallScreen"
                                    >
                                        <svg class="w-6 h-6 fill-current" viewBox="0 0 28 28" stroke="currentColor">
                                            <path
                                                d="M28 14c0 0.266-0.109 0.516-0.297 0.703l-4 4c-0.187 0.187-0.438 0.297-0.703 0.297-0.547 0-1-0.453-1-1v-2h-6v6h2c0.547 0 1 0.453 1 1 0 0.266-0.109 0.516-0.297 0.703l-4 4c-0.187 0.187-0.438 0.297-0.703 0.297s-0.516-0.109-0.703-0.297l-4-4c-0.187-0.187-0.297-0.438-0.297-0.703 0-0.547 0.453-1 1-1h2v-6h-6v2c0 0.547-0.453 1-1 1-0.266 0-0.516-0.109-0.703-0.297l-4-4c-0.187-0.187-0.297-0.438-0.297-0.703s0.109-0.516 0.297-0.703l4-4c0.187-0.187 0.438-0.297 0.703-0.297 0.547 0 1 0.453 1 1v2h6v-6h-2c-0.547 0-1-0.453-1-1 0-0.266 0.109-0.516 0.297-0.703l4-4c0.187-0.187 0.438-0.297 0.703-0.297s0.516 0.109 0.703 0.297l4 4c0.187 0.187 0.297 0.438 0.297 0.703 0 0.547-0.453 1-1 1h-2v6h6v-2c0-0.547 0.453-1 1-1 0.266 0 0.516 0.109 0.703 0.297l4 4c0.187 0.187 0.297 0.438 0.297 0.703z"
                                            ></path>
                                        </svg>
                                    </ButtonRounded>
                                    <ButtonRounded
                                        v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                                        @action="confirmStepToProduction(index)"
                                        title="Enviar a diseño"
                                        color="blue"
                                        large
                                    >
                                        <svg class="w-6 h-6 fill-current" viewBox="0 0 64 64" stroke="currentColor">
                                            <path
                                                d="M34.77 48.94c2.94-6.62 5.7-13.06 8.46-19.68 2.02-4.6 4.05-9.38 6.07-13.97 1.47-3.68.18-7.72-3.13-9.56-2.57-1.47-5.33-2.57-8.09-3.49-1.1-.37-2.39-.37-3.49-.18-3.31.55-4.96 2.94-6.25 5.88-3.31 7.91-6.8 15.63-10.11 23.54-1.29 3.13-2.76 6.07-3.86 9.01-.37.55-.37 1.47-.19 2.2.55 3.31.92 6.44 1.47 9.75.55 3.13.92 6.25 1.47 9.56.55-.37.92-.55 1.29-.74 4.96-3.31 9.93-6.8 14.71-10.3.73-.36 1.29-1.1 1.65-2.02zm-2.94 1.29c-2.76 1.84-5.52 3.86-8.27 5.7-.55.37-1.1.55-1.84.18-1.29-.55-2.57-1.1-3.86-1.84-.37-.18-.74-.55-.74-1.1-.55-3.49-1.1-6.99-1.65-10.48-.18-1.1 0-2.21.92-2.76.92-.37 2.02-.37 3.13-.18 1.29.37 1.84 1.29 2.21 3.13 2.57-1.65 5.33-.55 6.07 2.39.92-.18 1.84-.55 2.76-.55 2.39.18 3.68 2.39 2.57 4.23-.38.55-.75.91-1.3 1.28z"
                                            />
                                        </svg>
                                    </ButtonRounded>
                                    <ButtonRounded
                                        v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                                        @action="viewPlanning(index)"
                                        title="Ver solicitud"
                                        color="blue"
                                        large
                                    >
                                        <svg
                                            class="w-5 h-5 fill-current"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 283.5 283.5"
                                        >
                                            <path
                                                d="M275.2 50.3c-3.5-4.5-7.7-8.3-12.7-11.2-7.3-4.4-15.1-7.5-23.7-8.3-5.1-.5-10.2-.5-15.2.8-4.8 1.3-9 3.5-12.6 7-3.3 3.2-4.8 7.1-5.4 11.6-.1.9-.2 1.9-.4 2.8-.2 1.3-.5 1.6-1.9 1.7-2 .1-4 .1-6.1-.1-4.7-.3-9.3-.8-14-.7-1.6 0-3.2.1-4.7-.1-4.7-.5-9.3-.4-14-.6-5.8-.2-11.6-.1-17.4-.1-7.4 0-14.8-.1-22.1.2-5.7.2-11.5.7-17.2.5-.9 0-1.8.1-2.6.1-4.1.3-8.2.7-12.3.6-.9 0-1.9 0-2.8.1-4.5.4-8.9.7-13.4.7-.8 0-1.5 0-2.3.1-4.9.6-9.9.7-14.9 1.3-4 .5-8 1-11.9 1.9-5.5 1.2-10.8 3-15.9 5.3-9.1 4.1-16.1 10.5-20.9 19.3-4.7 8.8-6.4 18.4-6.5 28.3 0 1.4.3 2.9.3 4.3 0 2.7.5 5.4 1 8.1 1.3 6.6 3.5 13 6.5 19.1 3 6.1 6.5 11.8 10.7 17.1 3.6 4.5 7.5 8.8 11.9 12.7 7.2 6.4 15.1 11.6 23.9 15.4 6 2.6 12.2 4.8 18.6 6.2 3 .6 6 1.3 9.1 1.7 4.7.7 9.5.9 14.3 1.2 4.7.3 9.3-.1 14-.5 4.6-.3 9.3-1.1 13.8-2 5.7-1.1 11.2-2.6 16.6-4.6 10.8-4.1 20.5-10.1 28.3-18.7 4.2-4.6 7.4-9.8 8.7-16.1 1.2-5.9.5-11.5-2.1-16.9-1.9-4-4.5-7.6-7.9-10.6-2.8-2.4-5.9-4.3-9.3-5.7-7.8-3.2-16-3.4-24.2-2.8-5.9.4-11.7 2-17.2 4.1-9.1 3.5-17.3 8.6-23.9 15.9-3.1 3.5-5.6 7.3-7.2 11.7-.8 2.1-1.3 4.3-1.6 6.6-.2 1.8-1.2 2.8-2.9 3.3-2.6.7-5.1 1.3-7.8 1.6-6.5.8-12.6-.5-18.4-3.1-6-2.6-11-6.5-14.8-11.7-4.6-6.2-7.3-13.1-7.3-20.9v-4.2c0-6 1.8-11.4 4.9-16.5 4.3-6.8 10.6-11.1 17.8-14.2 5.2-2.3 10.7-3.7 16.3-4.8 5-1 10-1.5 15-2.1 2.6-.3 5.3-.3 7.9-.3 5.4.1 10.7-.1 16.1.3 5 .3 10.1-.1 15.1-.2 7.7-.1 15.4-.4 23.1-.7 8.3-.3 16.7-.2 25 .4 2.3.2 4.7.1 7 .4 5.1.6 10.1 1.2 15.1 2.5 4.3 1.1 8.6 2.4 12.6 4.6 5.3 2.9 9.1 7.1 11.4 12.7 2 4.9 3.1 10.1 3.8 15.4.7 5.4 1 10.8 1 16.2 0 4-.5 7.9-.4 11.9 0 .3 0 .5-.1.8-.7 5.2-1 10.5-2 15.7-.6 2.9-1.2 5.8-2.1 8.6-1.5 4.8-3.5 9.3-6 13.7-2.3 4-5.1 7.7-8.2 11.1-7.2 7.7-16.1 12.9-25.9 16.5-2.8 1.1-5.7 1.9-8.7 2.7-3.9 1.1-7.8 1.9-11.8 2.7-4 .7-8.1 1.2-12.1 1.8-4.5.6-9.1.7-13.6.9-7.1.3-14.2-.2-21.3-.9-4.5-.5-8.9-1.1-13.3-1.9-6.6-1.2-13-2.9-19.4-5-9-2.9-17.6-6.6-25.9-11-5.9-3.2-11.6-6.8-17.1-10.8-4.7-3.5-9.8-6.5-15.5-8-4-1.1-7.3 0-10.6 2.1-.9.6-1.8 1.4-2.5 2.3-2.8 3.6-2.8 7.6-2 11.7 1 5.1 3.5 9.6 6.6 13.7 3.3 4.3 7.5 7.5 11.8 10.5 5.3 3.7 10.9 6.9 16.7 9.6 6.5 3 13.2 5.7 20.1 7.9 6.6 2.1 13.2 3.7 19.9 5.2 2 .4 3.9.8 5.9 1.2 2.7.4 5.4.4 8 1.3.5.2 1 .2 1.5.2 3.2.1 6.3.6 9.4 1 6.2.7 12.3.7 18.5.9 4.1.1 8.2.1 12.3-.3 3.2-.3 6.4.1 9.6-.5 2.3-.4 4.7-.1 7-.6 2.9-.6 6-.2 8.7-1.5.2-.1.4-.1.6-.1 2.7-.2 5.4-.6 8-1.2 7-1.6 14-3.3 20.8-5.5 8.6-2.8 16.8-6.6 24.6-11.1 8.1-4.6 15.4-10.3 21.7-17.2 2.7-3 5.3-6.1 7.5-9.5 3.4-5.2 6-10.9 7.8-17 2.3-8.2 3.5-16.5 3.9-25 .5-10.8-.2-21.6-1.6-32.3-.6-4.1-1-8.3-2-12.3l-.9-4.5c-.8-4.2-1.7-8.4-2.8-12.5-.7-2.6-1.5-5.2-2.2-7.8-.4-1.5 0-2 1.4-2.3 1-.3 2.1-.5 3.1-.8 7.4-1.9 13.6-5.8 18.4-11.9 2.3-2.9 4.1-6.2 4.7-9.8 1.4-6.9-.5-13.2-4.8-18.8zM254.8 56c-1.7 6.6-10.1 7-14.7 4 0 0-2.5-10.2.7-12.8 3.2-2.6 7.4-1.1 7.4-1.1s8.2 3.3 6.6 9.9z"
                                            />
                                        </svg>
                                    </ButtonRounded>
                                    <ButtonRounded
                                        v-if="hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])"
                                        @action="openDiscontinueModal(index, item.idComercialRequest)"
                                        title="Descatalogar solicitud"
                                        color="red"
                                        large
                                    >
                                        <svg class="w-6 h-6 fill-current" viewBox="0 0 24 24" stroke="currentColor">
                                            <path
                                                d="M6 18L18 6M6 6L18 18"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </ButtonRounded>
                                </div>
                            </div>
                        </li>
                    </draggable>
                </ul>

                <PlanningPagination
                    :current-page="page"
                    :total-rows="totalRows"
                    :visible-rows="visibleRows"
                    @next-page="fetchPaginationData('next');"
                    @previous-page="fetchPaginationData('previous');"
                    @load-selected-page="fetchPaginationData"
                    @take-rows="takeRows"
                />
            </div>
        </section>

        <!-- === CALENDAR AND TOTAL PLANNINGS === -->
        <section class="w-full my-8 flex flex-wrap justify-between items-start">
            <!-- === CALENDAR === -->
            <div class="w-64 mx-auto sm:m-0 space-y-4">
                <v-calendar
                    is-inline
                    @dayclick="goToCalendar"
                    :attributes="attrs"
                    mode="multiple"
                    title-position="left"
                    @update:from-page="fetchCalendarTasks($event)"
                    v-if="hasRoles(['ADMIN', 'DIRECCIÓN', 'EQUIPO EUROPROSEM'])"
                />
                <ButtonRedirect route="calendar" v-if="hasRoles(['ADMIN', 'DIRECCIÓN', 'EQUIPO EUROPROSEM'])">
                    {{ $t("lang.msg.openCalendar") }}
                </ButtonRedirect>
            </div>
            <!-- TOTAL PLANNINGS  -->
            <div class="w-64 mx-auto mt-8 sm:m-0 text-center space-y-4">
                <div class="bg-white text-primario py-4 rounded-sm">
                    <span>Total visualizado</span>
                    <span class="text-4xl ml-2">{{ totalPlannings }}</span>
                </div>
                <ButtonAction @action="printPlanningOnPdf" large>
                    {{ $t("lang.msg.printList") }}
                </ButtonAction>
            </div>
        </section>

        <TheModal
            :open="isModalOpen"
            title="Descatalogar Solicitud comercial"
            @close="isModalOpen = false"
        >
            <template #body>
                <p class="mt-4 pr-16 text-gray-700 font-semibold">
                    {{ $t('lang.msg.discontinueComercialRequest') }}
                </p>
                <textarea
                    rows="2"
                    class="form-textarea w-full mt-2"
                    placeholder="Escriba el motivo aquí"
                    maxlength="256"
                    v-model="current.cancellationReason"
                />
                <span>{{ current.cancellationReason ? current.cancellationReason.length : 0 }}/256</span>
            </template>
            <template #action>
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
          <button
              type="button"
              class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-red-600 hover:bg-red-700 text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
              @click="discontinuePlanning"
          >{{ $t('lang.msg.discontinue') }}</button>
        </span>
            </template>
        </TheModal>
        <TheModal
            :open="isNextStepModalOpen"
            :title="$t('lang.msg.nextStep')"
            @close="isNextStepModalOpen=false"
        >
            <template #body>
                <Confirmation :to="'Diseño'"/>
            </template>
            <template #action>
      <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
        <button
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 hover:bg-green-700 text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
            @click="planningNextStep(planningIndex)"
        >Aceptar</button>
      </span>
            </template>
        </TheModal>
    </div>
</template>

<script>
// jsPDF
import jsPDF from "jspdf";
import "jspdf-autotable";

// MIXINS
import PlanningMixin from "@/components/mixins/PlanningMixin";
import TasksMixin from "@/components/mixins/TasksMixin";

// COMPONENTS
import TheModal from "@/components/shared/TheModal";
import PlanningDays from "@/components/shared/planning/PlanningDays";
import PlanningTableItem from "@/components/shared/planning/PlanningTableItem";
import PlanningPagination from "@/components/shared/planning/PlanningPagination";
import Confirmation from "@/components/modals/Confirmation";
import draggable from 'vuedraggable'

export default {
    name: "PlanningComercialRequest",
    components: {
        TheModal,
        PlanningDays,
        PlanningTableItem,
        PlanningPagination,
        Confirmation,
        draggable
    },
    mixins: [PlanningMixin, TasksMixin],
    data() {
        return {
            isModalOpen: false,
            homeFilter: 0,
            isNextStepModalOpen: false,
            planningIndex: 0
        };
    },
    created() {
        if (this.$route.params.fromHome) {
            this.title = this.$t("lang.msg.comercialRequestPlanningPendingDesign");
        } else {
            this.title = this.$t("lang.msg.comercialRequestPlanning");
        }

        this.homeFilter = this.$route.params.homeFilter;

        if (this.homeFilter == 1) {
            this.title = this.$t("lang.msg.comercialRequestPlanningDiscontinued");
            this.showingActivePlannings = false;
            this.setPaginationUrls(
                "api/ComercialRequests/PaginationDescatalogada",
                "api/ComercialRequests/TotalRegistrosGetDescatalogada"
            );
        } else {
            this.setPaginationUrls(
                "api/ComercialRequests/PaginationNoDesign",
                "api/ComercialRequests/TotalRegistrosNoDesign"
            );
        }

        this.fetchPaginationData();
        this.fetchCalendarTasks();
    },
    computed: {
        isSmallScreen() {
            return window.innerWidth < 1280
        }
    },
    methods: {
        changeFilter(v) {
            this.isFiltering = v !== undefined
        },
        confirmStepToProduction(i) {
            this.planningIndex = i
            this.isNextStepModalOpen = true;
        },
        filterAction() {
            if (this.homeFilter == 1) {
                this.filterPlannings('api/ComercialRequests/DiscontinuedGetBy', 'api/ComercialRequests/DiscontinuedTotalRegistrosGetBy')
            } else {
                this.filterPlannings('api/ComercialRequests/GetBy', 'api/ComercialRequests/TotalRegistrosGetBy')
            }
        },
        planningNextStep(index) {
            this.$axios
                .post("api/Designs/Add", {
                    idDesigner: this.planning[index].idClientDesigner,
                    idBaseColor: null,
                    idComercialRequest: this.planning[index].idComercialRequest,
                    idFactory: null,
                    idSize: null,
                    designToSample: false,
                    designToSampleDate: "2020-01-01T00:00:00.000Z",
                    designToSampleUser: null,
                    completado: false,
                    diseñador: null,
                    idUser: this.user.id
                })
                .then((response) => {
                    let idDesign = 0;
                    if (typeof response.data === "number") {
                        idDesign = response.data;
                    } else {
                        idDesign = response.data.idDesign;
                    }

                    // Push the route to 'Design' with 'idDesign'
                    this.$router.push({
                        name: "design",
                        params: {
                            idDesign: idDesign
                        }
                    });
                })
                .catch(() => this.showFriendlyError());
        },
        viewPlanning(index) {
            if (this.hasRoles(['DIRECCIÓN', 'ADMIN', 'EQUIPO EUROPROSEM'])) {
                this.$router.push({
                    name: "comercialRequest",
                    params: {
                        idComercialRequest: this.planning[index].idComercialRequest
                    }
                });
            }
        },
        openDiscontinueModal(index, idComercialRequest) {
            this.isModalOpen = true;
            this.current.index = index;
            this.current.idComercialRequest = idComercialRequest;
        },
        discontinuePlanning() {
            this.$axios
                .post("api/ComercialRequests/Descatalogo", {
                    idUser: this.user.id,
                    idComercialRequest: this.current.idComercialRequest,
                    comercialRequestCancellationReason: this.current.cancellationReason
                })
                .then(() => {
                    this.planning.splice(this.current.index, 1);
                    this.current = {};
                    this.isModalOpen = false;
                    this.openAlert("success", "Solicitud comercial descatalogada", null);
                })
                .catch(() => this.showFriendlyError());
        },
        printPlanningOnPdf() {
            // Only pt supported (not mm or in)
            var doc = new jsPDF("l", "pt");

            const head = [['', 'Referencia cliente', 'Fecha', 'Comercial Interno', 'Colección', 'Cliente', 'Diseñador cliente', 'Comprador', 'Artículo']]
            const body = [[]];
            doc.text("Planning solicitud comercial", 40, 30);
            let planningClone = [...this.planning];
            planningClone.forEach((element, index) => {
                body.push([]);
                body[index].push(index + 1);
                body[index].push(element.comercialRequestClientReference);
                body[index].push(element.comercialRequestActivationDate);
                body[index].push(element.nombreCompleto1);
                body[index].push(element.collectionEs);
                body[index].push(element.clientName);
                body[index].push(element.nombreCompleto3);
                body[index].push(element.nombreCompleto2);
                body[index].push(element.subfamilyArticleEs);
            })
            doc.autoTable({
                head: head,
                body: body,
                foot: [['Total: ' + planningClone.length]],
                styles: {fontSize: 6},
                theme: "striped",
                margin: {top: 40}
            });

            let currentDate = this.$moment().format("DDMMYYYY HHmmss");

            // Print task in PDF.
            doc.save(`PlanningSolicitud_${currentDate}.pdf`);
        }
    }
};
</script>
<style lang="css">
.border-red {
    border: 2px solid #ffc4c4;
}
</style>
